/**
 * Application — detail + stage-history timeline.  GET /applications/:id, PATCH /:id/stage
 * The candidate summary is pulled from GET /candidates/:id (also gives "other applications").
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Move, Phone, Mail, MapPin, Clock, CalendarDays } from 'lucide-react';
import { Button, Skeleton } from '../../components/ui';
import { Card } from '../../components/ui/Card';
import { useApplication, useMoveStage } from '../../features/recruitment/hooks/useApplications';
import { useCandidate } from '../../features/recruitment/hooks/useCandidates';
import {
	StageBadge,
	SourcePill,
	Avatar,
	CtcValue,
	StageTimeline,
	MoveStageDialog,
	ErrorState,
} from '../../features/recruitment';
import { formatDate, formatExperience, recruiterName } from '../../features/recruitment/utils/format';
import { getRecruitmentError } from '../../features/recruitment/utils/apiError';
import type { Stage } from '../../services/recruitmentApi';

const Field: React.FC<{ icon?: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
	<div>
		<p className='text-[11px] uppercase tracking-wide text-gray-400'>{label}</p>
		<p className='mt-0.5 flex items-center gap-1.5 text-sm font-medium text-gray-800'>
			{icon && <span className='text-gray-400'>{icon}</span>}
			{value}
		</p>
	</div>
);

export const ApplicationDetail: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const appId = id ? Number(id) : undefined;

	const { data, isLoading, isError, refetch } = useApplication(appId);
	const application = data?.application;
	const { data: candidateData } = useCandidate(application?.candidate_id);

	const moveStage = useMoveStage();
	const [moveOpen, setMoveOpen] = useState(false);
	const [moveError, setMoveError] = useState<string | null>(null);

	const candidate = candidateData?.candidate;
	const otherApplications = useMemo(
		() => (candidateData?.applications ?? []).filter(a => a.id !== appId),
		[candidateData, appId]
	);
	const currentRow = useMemo(
		() => (candidateData?.applications ?? []).find(a => a.id === appId),
		[candidateData, appId]
	);

	const confirmMove = async (to: Stage, reason: string) => {
		if (!appId) return;
		setMoveError(null);
		try {
			await moveStage.mutateAsync({ id: appId, body: { to, reason } });
			setMoveOpen(false);
			refetch();
		} catch (e) {
			setMoveError(getRecruitmentError(e).message);
		}
	};

	if (isLoading) {
		return (
			<div className='min-h-screen bg-gray-50 p-6'>
				<div className='mx-auto max-w-5xl space-y-4'>
					<Skeleton className='h-8 w-64' />
					<Skeleton className='h-40 w-full' />
					<div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
						<Skeleton className='h-64 w-full lg:col-span-2' />
						<Skeleton className='h-64 w-full' />
					</div>
				</div>
			</div>
		);
	}

	if (isError || !application) {
		return (
			<div className='min-h-screen bg-gray-50 p-6'>
				<div className='mx-auto max-w-5xl'>
					<ErrorState message='We could not load this application.' onRetry={() => refetch()} />
				</div>
			</div>
		);
	}

	const candidateName = currentRow?.candidate_name ?? candidate?.full_name ?? `Candidate #${application.candidate_id}`;
	const jobTitle = currentRow?.job_title ?? `Opening #${application.job_opening_id}`;

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='mx-auto max-w-5xl'>
				<button
					type='button'
					onClick={() => navigate('/recruitment/applications')}
					className='mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800'
				>
					<ArrowLeft className='h-4 w-4' /> Applications
				</button>

				{/* Header */}
				<Card className='mb-6'>
					<div className='flex flex-wrap items-start justify-between gap-4'>
						<div className='flex items-start gap-3'>
							<Avatar name={candidateName} />
							<div>
								<button
									type='button'
									onClick={() => navigate(`/recruitment/candidates/${application.candidate_id}`)}
									className='text-xl font-semibold text-gray-900 hover:text-primary'
								>
									{candidateName}
								</button>
								<p className='mt-0.5 text-sm text-gray-500'>
									for{' '}
									<button
										type='button'
										onClick={() => navigate(`/recruitment/jobs/${application.job_opening_id}`)}
										className='font-medium text-gray-700 hover:text-primary'
									>
										{jobTitle}
									</button>
								</p>
							</div>
						</div>
						<Button variant='primary' leftIcon={<Move className='h-4 w-4' />} onClick={() => { setMoveError(null); setMoveOpen(true); }}>
							Move stage
						</Button>
					</div>

					<div className='mt-5 grid grid-cols-2 gap-4 border-t border-gray-100 pt-5 md:grid-cols-4'>
						<Field label='Stage' value={<StageBadge stage={application.stage} />} />
						<Field label='Source' value={<SourcePill source={application.source} />} />
						<Field icon={<CalendarDays className='h-3.5 w-3.5' />} label='Applied' value={formatDate(application.applied_date)} />
						<Field label='Recruiter' value={recruiterName(currentRow?.recruiter_first_name, currentRow?.recruiter_last_name)} />
					</div>
				</Card>

				<div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
					{/* Left: candidate summary + other applications */}
					<div className='space-y-6 lg:col-span-1'>
						<Card>
							<h2 className='mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500'>Candidate</h2>
							{candidate ? (
								<div className='space-y-3'>
									{candidate.phone_raw && <Field icon={<Phone className='h-3.5 w-3.5' />} label='Phone' value={candidate.phone_raw} />}
									{candidate.email_raw && <Field icon={<Mail className='h-3.5 w-3.5' />} label='Email' value={candidate.email_raw} />}
									{candidate.location && <Field icon={<MapPin className='h-3.5 w-3.5' />} label='Location' value={candidate.location} />}
									<Field label='Experience' value={formatExperience(candidate.total_experience)} />
									{(candidate.current_role || candidate.current_company) && (
										<Field label='Current' value={[candidate.current_role, candidate.current_company].filter(Boolean).join(' · ')} />
									)}
									{candidate.notice_period_days != null && (
										<Field icon={<Clock className='h-3.5 w-3.5' />} label='Notice period' value={`${candidate.notice_period_days} days`} />
									)}
									<div className='grid grid-cols-2 gap-3 border-t border-gray-100 pt-3'>
										<Field label='Current CTC' value={<CtcValue candidate={candidate} field='current_ctc' />} />
										<Field label='Expected CTC' value={<CtcValue candidate={candidate} field='expected_ctc' />} />
									</div>
									<Button variant='outline' size='sm' fullWidth onClick={() => navigate(`/recruitment/candidates/${candidate.id}`)}>
										View full profile
									</Button>
								</div>
							) : (
								<div className='space-y-2'>
									<Skeleton className='h-4 w-full' />
									<Skeleton className='h-4 w-3/4' />
									<Skeleton className='h-4 w-2/3' />
								</div>
							)}
						</Card>

						{otherApplications.length > 0 && (
							<Card>
								<h2 className='mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500'>Other applications</h2>
								<ul className='divide-y divide-gray-100'>
									{otherApplications.map(a => (
										<li key={a.id}>
											<button
												type='button'
												onClick={() => navigate(`/recruitment/applications/${a.id}`)}
												className='flex w-full items-center justify-between py-2.5 text-left hover:bg-gray-50'
											>
												<div className='min-w-0'>
													<p className='truncate text-sm font-medium text-gray-900'>{a.job_title}</p>
													<p className='text-xs text-gray-400'>Applied {formatDate(a.applied_date)}</p>
												</div>
												<StageBadge stage={a.stage} />
											</button>
										</li>
									))}
								</ul>
							</Card>
						)}
					</div>

					{/* Right: stage history timeline */}
					<div className='lg:col-span-2'>
						<Card>
							<h2 className='mb-4 text-base font-semibold text-gray-900'>Stage history</h2>
							<StageTimeline history={data?.stage_history ?? []} />
						</Card>
					</div>
				</div>
			</div>

			<MoveStageDialog
				open={moveOpen}
				onClose={() => { setMoveOpen(false); setMoveError(null); }}
				currentStage={application.stage}
				candidateName={candidateName}
				loading={moveStage.isPending}
				error={moveError}
				onConfirm={confirmMove}
			/>
		</div>
	);
};
